import { DEFAULT_ANTI_SPIDER, METRIC_NAME, METRIC_NAME_MONITOR_JS_LOAD_TIME, METRIC_NAME_REWRITE } from '@/constants/index.ts';
import GlobalVariables, { GLOBAL_VARIABLES } from './global.ts';
import logUtils from './logUtils.ts';
import { reportImmediate, reportDeferred } from '@/utils/metricHelper';

export const fetchWithRetries = async (url: string, options: RequestInit = {}, maxRetries: number = 1, backoff: number = 1000): Promise<Response | any> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fetch(url, options)
    } catch (error) {
      if (attempt === maxRetries - 1) throw error;
      await new Promise(r => setTimeout(r, backoff * Math.pow(2, attempt)))
    }
  }
}

export const rewriteFetch = (_headers: string[], _sdks: string[]) => {
  const remove_headers = _headers || []
  if (typeof window !== 'undefined') {
    const originalFetch = window.fetch;
    window.fetch = async function (input, init = {}) {
      let url = input as string;
      let method = 'GET';
      let headers: Headers | any;
      let body: BodyInit | null | undefined;
      let pathname = '';
      let search = '';

      try {
        // 如果 input 是一个 Request 对象，而非字符串 URL，则需要解析
        if (input instanceof Request) {
          url = input.url;
          method = input.method;
          headers = new Headers(input.headers);
          body = input.body;
        } else {
          url = input as string;
          method = (init?.method || 'GET').toUpperCase();
          headers = init?.headers ? new Headers(init.headers) : new Headers();
          body = init?.body;
        }

        try {
          const urlObj = new URL(url, location.origin);
          pathname = urlObj.pathname;
          search = urlObj.search;
        } catch {
          pathname = '';
          search = '';
        }

        const customHeaders: Record<string, string> = {};

        // 处理 anti-in header
        if (remove_headers.includes('anti_in')) {
          const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
          // const antiIn = getAntiIn()
          const antiIn = await getAsyncAntiIn()
          if (antiIn && hit?.some(path => url.includes(path))) {
            customHeaders['anti-in'] = antiIn;
          }
        } else {
          const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
          const antiHit = antiHitFromWindow()
          const globalHit = GlobalVariables?.get(GLOBAL_VARIABLES.ANTI_HIT) || []
          const hits = Array.from(new Set([...hit, ...globalHit, ...antiHit]))
          // const antiIn = getAntiIn()
          const antiIn = await getAsyncAntiIn()
          if (antiIn && hits?.some(path => url.includes(path))) {
            customHeaders['anti-in'] = antiIn;
          }
        }

        // 处理站内请求的自定义 headers
        if (isInSite(url)) {
          let channel = GlobalVariables.get(GLOBAL_VARIABLES.CHANNEL)

          // 处理 armorToken
          if (channel == 0 || channel == 1) {
            // pc pwa
            const at = getArmorToken() || ''
            if (at) {
              customHeaders['armorToken'] = at;
            }
          } else {
            // h5
            const at = window?.appComParams?.armorToken || window?.appComParams?.armortoken || window?.appComParams?.ArmorToken || window._s_ArmorToken || ''
            if (at) {
              customHeaders['armorToken'] = at;
            }
          }

          // 处理 x-gw-auth 签名
          const url2 = `${pathname}${search}` || '';
          try {
            if (url2 && window?._SHEIN_CALC_SIGNATURE_) {
              customHeaders['x-gw-auth'] = await window._SHEIN_CALC_SIGNATURE_(url2);
            }
          } catch (error) {
            reportDeferred({
              metric_name: METRIC_NAME,
              tags: { url: url2, type: 'fetchCalcSignatureError' },
              value: 1,
              message: 'Failed to fetch calculate signature',
              params: error
            });
          }

          // 处理 smDeviceId
          if (channel == 0 || channel == 1) {
            // pc pwa
            const sm = getSmDeviceId() || '';
            if (sm) {
              customHeaders['smDeviceId'] = sm;
            }
          } else {
            // h5
            const sm = window?.appComParams?.SmDeviceId || window?.appComParams?.smDeviceId || window?.appComParams?.smdeviceid || window._s_SmDeviceId || ''
            if (sm) {
              customHeaders['smDeviceId'] = sm;
            }
          }

          // 步骤3: 将自定义 headers 设置到请求中
          Object.entries(customHeaders).forEach(([key, value]) => {
            if (value) {
              headers.set(key, value);
            }
          });
          if (_sdks?.includes('csrandom')) {
            // const hit_cs = DEFAULT_ANTI_SPIDER?.csrandom?.hit || []
            // const globalHitCS = GlobalVariables?.get(GLOBAL_VARIABLES.CS_HIT) || []
            // const hits_cs = Array.from(new Set([...hit_cs, ...globalHitCS]))
            // if (hits_cs?.some(path => url.includes(path))) {
            const v = window?.CSRandom?.rand(search.substring(1), body, headers) || 'sdk error'
            headers.set('x-cs-random', v);
            // }
          }
        }
      } catch (error) {
        reportDeferred({
          metric_name: METRIC_NAME_REWRITE,
          tags: { type: 'fetchError' },
          value: 1,
          message: 'Failed to rewrite fetch',
          params: error
        });
      }

      const { headers: _ignored, ...restInit } = init as any;
      const options = {
        ...restInit,
        headers,
        method,
        body
      };

      const response = await originalFetch(url, options);
      return response;
    };
  }
}

export const rewriteXHR = (_headers: string[], _sdks: string[], _mirror: { [key: string]: string } = {}) => {
  const remove_headers = _headers || []
  let atKey = _mirror['armorToken'] || 'armorToken'
  let smKey = _mirror['smDeviceId'] || 'smDeviceId'
  if (typeof window !== 'undefined') {
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function () {
      try {
        this.__pendingHeaders = new Map<string, string>();
        this.__headersFlushed = false;
        this._method = arguments[0];
        this._url = arguments[1];
        // 记录是否为同步请求（第三个参数 async，默认为 true）
        this._isAsync = arguments[2] !== false;
        try {
          const urlObj = new URL(this._url || '', location.origin);
          this._pathname = urlObj.pathname;
          this._search = urlObj.search;
        } catch {
          this._pathname = '';
          this._search = '';
        }
        this._csrandomHeaders = {}; // 加签 headers
        // if (remove_headers.includes('anti_in')) {
        //   const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
        //   if (hit?.some(path => arguments[1].includes(path))) {
        //     this._customHeaders = {
        //       'anti-in': getAntiIn()
        //     };
        //   }
        // } else {
        //   const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
        //   const antiHit = antiHitFromWindow()
        //   const globalHit = GlobalVariables?.get(GLOBAL_VARIABLES.ANTI_HIT) || []
        //   const hits = Array.from(new Set([...hit, ...globalHit, ...antiHit]))
        //   if (hits?.some(path => arguments[1].includes(path))) {
        //     this._customHeaders = {
        //       'anti-in': getAntiIn()
        //     };
        //   }
        // }
        if (isInSite(arguments[1])) {
          let channel = GlobalVariables.get(GLOBAL_VARIABLES.CHANNEL)
          if (channel == 0 || channel == 1) {
            // pc pwa
            const at = getArmorToken() || ''
            if (at) {
              this._customHeaders = {
                ...this._customHeaders,
                // 'armorToken': at,
                [atKey]: at,
              };
            }
          } else {
            // h5
            const at = window?.appComParams?.armorToken || window?.appComParams?.armortoken || window?.appComParams?.ArmorToken || window._s_ArmorToken || ''
            if (at) {
              this._customHeaders = {
                ...this._customHeaders,
                //  'armorToken': at,
                [atKey]: at,
              };
            }
          }
          const sigUrl = getSigUrl(arguments[1])
          if (sigUrl) {
            this._sigUrl = sigUrl;
          }
          if (channel == 0 || channel == 1) {
            // pc pwa
            const sm = getSmDeviceId() || '';
            if (sm) {
              this._customHeaders = {
                ...this._customHeaders,
                // 'smDeviceId': sm,
                [smKey]: sm
              };
            }
          } else {
            // h5
            const sm = window?.appComParams?.SmDeviceId || window?.appComParams?.smDeviceId || window?.appComParams?.smdeviceid || window._s_SmDeviceId || ''
            if (sm) {
              this._customHeaders = {
                ...this._customHeaders,
                // 'smDeviceId': sm,
                [smKey]: sm
              };
            }
          }
        }
      } catch (error) {
        // 捕获所有异常，防止抛出导致请求失败
        reportImmediate({
          metric_name: METRIC_NAME_REWRITE,
          tags: { type: 'xhrOpenError' },
          value: 1,
          message: 'Failed to open XHR',
          params: error
        });
      }
      originalOpen.apply(this, arguments as any);
    };
    const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
    XMLHttpRequest.prototype.setRequestHeader = function (name: string, value: string) {
      if (!this._csrandomHeaders) {
        this._csrandomHeaders = {};
      }
      this._csrandomHeaders[name] = value;
      // 初始化缓存
      if (!this.__pendingHeaders) {
        this.__pendingHeaders = new Map<string, string>();
      }

      const lowerName = name.toLowerCase();

      // 定义需要启用覆盖模式的请求头
      // const overrideHeaders = ['anti-in', 'armortoken', 'x-gw-auth', 'smdeviceid', 'x-cs-random'];
      const overrideHeaders = ['anti-in', atKey.toLowerCase(), 'x-gw-auth', smKey.toLowerCase(), 'x-cs-random'];

      if (overrideHeaders.includes(lowerName)) {
        // 覆盖模式：后来的值覆盖前面的值
        if (this.__pendingHeaders.has(lowerName)) {
          logUtils.warn('Header', `${name} override: ${this.__pendingHeaders.get(lowerName)} -> ${value}`);
        }
        // 存入缓存，不立即调用原生方法
        if (value) {
          this.__pendingHeaders.set(lowerName, value);
          this._csrandomHeaders[name] = value;
        }
      } else {
        // 其他请求头：直接调用原生方法（保持标准行为）
        originalSetRequestHeader.call(this, name, value);
      }
    };

    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      try {
        if (!this.__pendingHeaders) {
          this.__pendingHeaders = new Map<string, string>();
        }
        if (!this._csrandomHeaders) {
          this._csrandomHeaders = {};
        }
        // if (this?._customHeaders?.['anti-in'] && !this.__pendingHeaders.has('anti-in')) {
        //   this.__pendingHeaders.set('anti-in', this?._customHeaders?.['anti-in']);
        //   // this.setRequestHeader('anti-in', this?._customHeaders?.['anti-in']);
        //   this._csrandomHeaders['anti-in'] = this?._customHeaders?.['anti-in'];
        // }
        // if (this?._customHeaders?.['armorToken']) {
        //   this.__pendingHeaders.set('armortoken', this?._customHeaders?.['armorToken']);
        //   // this.setRequestHeader('armorToken', this?._customHeaders?.['armorToken']);
        //   this._csrandomHeaders['armorToken'] = this?._customHeaders?.['armorToken'];
        // }
        // if (this?._customHeaders?.['smDeviceId']) {
        //   this.__pendingHeaders.set('smdeviceid', this?._customHeaders?.['smDeviceId']);
        //   // this.setRequestHeader('smDeviceId', this?._customHeaders?.['smDeviceId']);
        //   this._csrandomHeaders['smDeviceId'] = this?._customHeaders?.['smDeviceId'];
        // }
        if (this?._customHeaders?.[atKey] && !this.__pendingHeaders.has(atKey) && !this.__pendingHeaders.has(atKey.toLowerCase())) {
          this.__pendingHeaders.set(atKey, this?._customHeaders?.[atKey]);
          // this.setRequestHeader('armorToken', this?._customHeaders?.[atKey]);
          this._csrandomHeaders[atKey] = this?._customHeaders?.[atKey];
        }
        if (this?._customHeaders?.[smKey] && !this.__pendingHeaders.has(smKey) && !this.__pendingHeaders.has(smKey.toLowerCase())) {
          this.__pendingHeaders.set(smKey, this?._customHeaders?.[smKey]);
          // this.setRequestHeader('smDeviceId', this?._customHeaders?.[smKey]);
          this._csrandomHeaders[smKey] = this?._customHeaders?.[smKey];
        }
        if (this._isAsync) {
          // 异步请求：使用异步签名函数
          const self = this;
          const args = arguments;
          (async () => {
            try {
              if (!self._csrandomHeaders) {
                self._csrandomHeaders = {};
              }
              if (!self.__pendingHeaders) {
                self.__pendingHeaders = new Map<string, string>();
              }
              //anti
              if (!self.__pendingHeaders.has('anti-in')) {
                if (remove_headers.includes('anti_in')) {
                  const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
                  if (hit?.some(path => self._url?.includes(path))) {
                    const _anti = await getAsyncAntiIn()
                    self.__pendingHeaders.set('anti-in', _anti);
                    self._csrandomHeaders['anti-in'] = _anti;
                  }
                } else {
                  const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
                  const antiHit = antiHitFromWindow()
                  const globalHit = GlobalVariables?.get(GLOBAL_VARIABLES.ANTI_HIT) || []
                  const hits = Array.from(new Set([...hit, ...globalHit, ...antiHit]))
                  if (hits?.some(path => self._url?.includes(path))) {
                    const _anti = await getAsyncAntiIn()
                    self.__pendingHeaders.set('anti-in', _anti);
                    self._csrandomHeaders['anti-in'] = _anti;
                  }
                }
              }
              // main
              if (self._sigUrl) {
                const signature = await calcSignatureAsync(self._sigUrl);
                if (signature) {
                  self?.__pendingHeaders?.set('x-gw-auth', signature);
                  self._csrandomHeaders['x-gw-auth'] = signature;
                }
              }
              if (_sdks?.includes('csrandom') && isInSite(self?._url || '')) {
                // const hit_cs = DEFAULT_ANTI_SPIDER?.csrandom?.hit || []
                // const globalHitCS = GlobalVariables?.get(GLOBAL_VARIABLES.CS_HIT) || []
                // const hits_cs = Array.from(new Set([...hit_cs, ...globalHitCS]))
                // if (hits_cs?.some(path => self._url?.includes(path))) {
                const v = window?.CSRandom?.rand(self._search?.substring(1) || '', body, self._csrandomHeaders) || 'sdk error'
                self?.__pendingHeaders?.set('x-cs-random', v);
                // }
              }
              // 在签名计算完成后调用原始 send
              if (!self.__headersFlushed && self?.__pendingHeaders && self?.__pendingHeaders?.size > 0) {
                self.__pendingHeaders.forEach((value, name) => {
                  // 调用原生方法，设置最终的请求头
                  originalSetRequestHeader.call(self, name, value);
                });
                self.__headersFlushed = true;  // 标记已刷新
              }
            } catch (error) {
              // 捕获所有异常，防止抛出导致请求失败
              reportImmediate({
                metric_name: METRIC_NAME_REWRITE,
                tags: { type: 'xhrSendError' },
                value: 1,
                message: 'Failed to send XHR',
                params: error
              });
            }
            originalSend.apply(self, args as any);
          })();
          // 提前返回，避免重复调用 originalSend
          return;
        } else {
          // 同步请求：签名JS无同步方法 暂不支持
          if (!this.__pendingHeaders.has('anti-in')) {
            // 同步请求
            if (remove_headers.includes('anti_in')) {
              const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
              if (hit?.some(path => this._url?.includes(path))) {
                const _anti = getAntiIn()
                this.__pendingHeaders.set('anti-in', _anti);
                this._csrandomHeaders['anti-in'] = _anti;
              }
            } else {
              const hit = DEFAULT_ANTI_SPIDER?.anti_in?.hit || []
              const antiHit = antiHitFromWindow()
              const globalHit = GlobalVariables?.get(GLOBAL_VARIABLES.ANTI_HIT) || []
              const hits = Array.from(new Set([...hit, ...globalHit, ...antiHit]))
              if (hits?.some(path => this._url?.includes(path))) {
                const _anti = getAntiIn()
                this.__pendingHeaders.set('anti-in', _anti);
                this._csrandomHeaders['anti-in'] = _anti;
              }
            }
          }
          if (_sdks?.includes('csrandom') && isInSite(this?._url || '')) {
            // const hit_cs = DEFAULT_ANTI_SPIDER?.csrandom?.hit || []
            // const globalHitCS = GlobalVariables?.get(GLOBAL_VARIABLES.CS_HIT) || []
            // const hits_cs = Array.from(new Set([...hit_cs, ...globalHitCS]))
            // if (hits_cs?.some(path => this._url?.includes(path))) {
            const v = window?.CSRandom?.rand(this._search?.substring(1) || '', body, this._csrandomHeaders) || 'sdk error'
            this.__pendingHeaders.set('x-cs-random', v);
            // }
          }
        }

        if (!this.__headersFlushed && this.__pendingHeaders.size > 0) {
          this.__pendingHeaders.forEach((value, name) => {
            // 调用原生方法，设置最终的请求头
            originalSetRequestHeader.call(this, name, value);
          });
          this.__headersFlushed = true;  // 标记已刷新
        }
      } catch (err) {
        // 捕获所有异常，防止抛出导致请求失败
        reportImmediate({
          metric_name: METRIC_NAME_REWRITE,
          tags: { type: 'xhrSendError' },
          value: 1,
          message: 'Failed to send XHR',
          params: err
        });
      }
      // 同步请求或不需要签名的情况下，直接调用原始 send
      originalSend.apply(this, arguments as any);
    };
  }
}


// 计算签名 - 异步版本
export const calcSignatureAsync = async (url: string): Promise<string | null> => {
  try {
    if (!window._SHEIN_CALC_SIGNATURE_) {
      return null;
    }
    return await window._SHEIN_CALC_SIGNATURE_(url);
  } catch (error) {
    reportImmediate({
      metric_name: METRIC_NAME,
      tags: { url, type: 'xhrCalcSignatureError' },
      value: 1,
      message: 'Failed to xhr calculate signature',
      params: error
    });
    return null;
  }
}
// get anti-in
export const getAntiIn = () => {
  const AntiIn = window.AntiIn
  if (!AntiIn) return ''
  if (AntiIn._last) return AntiIn._last
  let anti_channel = 4; // h5
  let channel = GlobalVariables.get(GLOBAL_VARIABLES.CHANNEL);
  if (channel == 0 || channel == 1) {
    // pc pwa
    anti_channel = parseInt(channel);
  }
  AntiIn._calculating = AntiIn.syncGetAllEncrypted(anti_channel)
  AntiIn._last = AntiIn._calculating
  setTimeout(() => AntiIn._last = undefined, 200)
  return AntiIn._calculating
}

// 异步获取 anti-in
export async function getAsyncAntiIn() {
  const AntiIn = window.AntiIn
  if (!AntiIn) return ''
  if (AntiIn._last) return AntiIn._last
  let anti_channel = 4; // h5
  let channel = GlobalVariables.get(GLOBAL_VARIABLES.CHANNEL);
  if (channel == 0 || channel == 1) {
    // pc pwa
    anti_channel = parseInt(channel);
  }
  AntiIn._calculating = AntiIn.getAllEncrypted(anti_channel)
    .then((_: string) => {
      AntiIn._calculating = null
      setTimeout(() => AntiIn._last = undefined, 200)
      return AntiIn._last = _
    })
    .catch(() => '')
  return await AntiIn._calculating
}

const isInSite = (url: string) => {
  if (!url) {
    return false
  }
  if (DEFAULT_ANTI_SPIDER?.anti_in?.hit?.some((path: string) => url?.includes(path))) {
    // 命中SDK路径
    return true
  }
  if (DEFAULT_ANTI_SPIDER?.csrandom?.hit?.some((path: string) => url?.includes(path))) {
    // 命中SDK路径
    return true
  }
  // if (/^http(s)?:\/\/www\.srmdata.com/.test(url)) {
  //   return true
  // }
  if (!/^http(s)?:\/\//.test(url)) { // 相对路径
    return true
  }
  const regExp = new RegExp(`^http(s)?://${location.host}`)
  if (regExp.test(url)) { // 本站地址
    return true
  }
  return false
}

const getArmorToken = () => {
  if (window.__ArmorToken) return window.__ArmorToken;
  window.__ArmorToken = window?.localStorage?.getItem('armorToken') || '';
  return window.__ArmorToken;
}

const getSmDeviceId = () => {
  if (window.__SmDeviceId) return window.__SmDeviceId;
  window.__SmDeviceId = window?.localStorage?.getItem('SmDeviceId') || '';
  return window.__SmDeviceId;
}

const getSigUrl = (url: string) => {
  // let sigUrl = url
  // let _query = sigUrl?.split('?')?.[1]?.replace(/'/g, '%27') || ''
  // if (_query) {
  //   sigUrl = sigUrl?.split('?')?.[0] + '?' + _query
  // } else {
  //   sigUrl = sigUrl?.split('?')?.[0]
  // }
  // return sigUrl
  const { pathname, search } = new URL(url, location.origin);
  return `${pathname}${search}` || '';
}

export const fetchJsonSafe = async (url: string, opts = {}) => {
  const res = await fetch(url, opts);
  if (!res.ok) {
    const raw = await res.text().catch(() => '[unreadable body]');
    const err: any = new Error(`HTTP ${res.status} ${res.statusText}`);
    err.status = res.status;
    err.raw = raw;
    throw err;
  }

  // 检查 content-type 是否声明为 JSON
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (!ct.includes('application/json')) {
    const raw = await res.text().catch(() => '[unreadable body]');
    const err: any = new Error('Response is not application/json');
    err.raw = raw;
    err.contentType = ct;
    err.status = res.status;
    throw err;
  }

  try {
    const r = { response: await res.json(), status: res.status }
    return r;
  } catch (parseErr: any) {
    // res.json() 失败时，使用 clone() 读取原始文本
    const raw = await res.clone().text().catch(() => '[unreadable body]');
    const err: any = new Error('Invalid JSON response: ' + parseErr.message);
    err.original = parseErr;
    err.raw = raw;
    err.status = res.status;
    throw err;
  }
}

export const monitorSingleJsLoadTime = (keyword: string, assistName: string, assistKeyword?: string) => {
  if (!window.performance || !window.performance.getEntriesByType) return;
  const scriptEntries = window.performance.getEntriesByType('resource')
    .filter(entry => (entry as PerformanceResourceTiming).initiatorType === 'script');
  scriptEntries.forEach(entry => {
    const jsName = entry?.name?.split('/')?.slice(-1)?.[0] || '';
    if ((jsName.includes(keyword) && entry.name.includes(assistName))) {
      reportDeferred({
        metric_name: METRIC_NAME_MONITOR_JS_LOAD_TIME,
        tags: { type: 'mjlt', url: entry.name, name: assistKeyword || jsName, duration: entry?.duration?.toFixed(0) },
        value: 1,
        message: 'monitorJsLoadTime',
        params: JSON.parse(JSON.stringify(entry || {}))
      })
    }
  });
};

// loader.js（入口，兼容所有浏览器）
async function dynamicLoadScript(url: string) {
  const code = await fetch(url).then((r) => r.text());

  if (supportsDynamicImport()) {
    console.log('Loading script via dynamic import:', url);
    await loadViaImport(code); // 动态加载这个函数
  } else {
    console.log('Loading script via new Function:', url);
    new Function(code)();
  }
}

function supportsDynamicImport() {
  try {
    new Function('return import("")');
    return true;
  } catch (e) {
    console.warn('Dynamic import is not supported in this environment.', e);
    return false;
  }
}

// 把含有 import() 语法的代码单独隔离，走 new Function 执行
async function loadViaImport(code: string) {
  const fn = new Function(
    "code",
    `                                                                                                                                                                                             
return (async () => {                                                                                                                                                                                                       
        const blob = new Blob([code], { type: 'application/javascript' });                                                                                                                                                        
        const url = URL.createObjectURL(blob);                                                                                                                                                                                    
        try { await import(url); } finally { URL.revokeObjectURL(url); }                                                                                                                                                          
      })();                                                                                                                                                                                                                  
    `
  );
  await fn(code);
}


export { dynamicLoadScript };
// loader.js（入口，兼容所有浏览器）

function antiHitFromWindow() {
  const spiderAntiIn = window?.__spider_config|| []
  return spiderAntiIn?.find(item => item.name === 'anti_in')?.config?.hit || []
}